import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from 'ahooks';

import { FN_OPTIONS } from 'application/consts';
import { useAccountCompanyId } from 'application/hooks';
import useDropdownPlacement from 'application/hooks/useDropdownPlacement';
import { usePlayerContext } from 'application/providers/PlayerProvider';

import { AdCampaignStatusesEnum, PromoClipsQuerySort } from '@shared/api/graphql/generated';

import {
  DATA_TEST_IDS,
  PROMOS_DROPDOWN_MAX_HEIGHT,
  PROMOS_DROPDOWN_MIN_HEIGHT,
  PROMOS_DROPDOWN_PREFERRED_HEIGHT,
  PROMOS_DROPDOWN_VIEWPORT_GAP,
} from '../consts';
import { PROMO_DROPDOWN_GAP } from './consts';
import type { PromoFieldShellProps, PromoListItem } from './types';
import { usePromoClipsList } from '../../ReportRequestForm/PromoSelector/usePromoClipsList';
import { usePromoDropdownControls } from '../../ReportRequestForm/PromoSelector/usePromoDropdownControls';
import { usePromoSelectionActions } from './usePromoSelectionActions';
import { getPromoDropdownTopBoundary } from './utils';

type UsePromoFieldShellViewModelOptions = Pick<
  PromoFieldShellProps,
  | 'draftError'
  | 'draftId'
  | 'isDraftLoading'
  | 'onRetryDraftCreate'
  | 'selectedCount'
  | 'setSelectedCount'
  | 'totalCount'
>;

export const usePromoFieldShellViewModel = ({
  draftError,
  draftId,
  isDraftLoading,
  onRetryDraftCreate,
  selectedCount,
  setSelectedCount,
  totalCount,
}: UsePromoFieldShellViewModelOptions) => {
  const companyId = useAccountCompanyId();
  const { play } = usePlayerContext();
  const {
    containerRef,
    filterButtonRef,
    filterContainerRef,
    isDropdownOpen,
    isFilterOpen,
    triggerRef,
    onDropdownToggle,
    onFilterToggle,
  } = usePromoDropdownControls();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(PromoClipsQuerySort.CreatedDesc);
  const [selectedStatuses, setSelectedStatuses] = useState<AdCampaignStatusesEnum[]>([]);
  const searchQuery = useDebounce(search.trim(), FN_OPTIONS);
  const filters = useMemo(() => {
    const nextFilters = {
      ...(searchQuery ? { query: searchQuery } : {}),
      ...(selectedStatuses.length > 0 ? { statuses: selectedStatuses } : {}),
    };

    return Object.keys(nextFilters).length > 0 ? nextFilters : undefined;
  }, [searchQuery, selectedStatuses]);
  const queryKey = useMemo(
    () => [companyId, draftId, searchQuery, sort, selectedStatuses.join(',')].join('|'),
    [companyId, draftId, searchQuery, selectedStatuses, sort]
  );
  const {
    hasNextPage,
    hasLoadedFirstPage,
    isInitialLoading,
    isNextPageLoading,
    items,
    queryError: listQueryError,
    statusesInfo,
    applyPromoSelection,
    clearPromoSelectionOverrides,
    requestFirstPage,
    onLoadMore,
    onRetry,
  } = usePromoClipsList(companyId, draftId, filters, isDraftLoading, isDropdownOpen, queryKey, sort);
  const requestFirstPageRef = useRef(requestFirstPage);

  useEffect(() => {
    requestFirstPageRef.current = requestFirstPage;
  }, [requestFirstPage]);

  const onRefreshCurrentFirstPage = useCallback(() => {
    requestFirstPageRef.current();
  }, []);
  const { isBulkSelectionActionPending, isSelectionActionPending, onPromoSelectionChange, onReset, onSelectAll } =
    usePromoSelectionActions(
      companyId,
      draftId,
      filters,
      isInitialLoading,
      selectedCount,
      onRefreshCurrentFirstPage,
      applyPromoSelection,
      clearPromoSelectionOverrides,
      setSelectedCount
    );
  const dropdownRepositionKey = `${isDraftLoading}:${isInitialLoading}:${items.length}`;
  const getTopBoundary = useCallback(
    (fieldElement: HTMLElement) => getPromoDropdownTopBoundary(fieldElement, PROMOS_DROPDOWN_VIEWPORT_GAP),
    []
  );
  const dropdownState = useDropdownPlacement({
    containerRef: triggerRef,
    getTopBoundary,
    isOpen: isDropdownOpen,
    repositionKey: dropdownRepositionKey,
    gap: PROMO_DROPDOWN_GAP,
    maxHeight: PROMOS_DROPDOWN_MAX_HEIGHT,
    minHeight: PROMOS_DROPDOWN_MIN_HEIGHT,
    preferredHeight: PROMOS_DROPDOWN_PREFERRED_HEIGHT,
    viewportGap: PROMOS_DROPDOWN_VIEWPORT_GAP,
  });

  const onSortToggle = useCallback(() => {
    setSort((currentSort) =>
      currentSort === PromoClipsQuerySort.CreatedDesc ? PromoClipsQuerySort.CreatedAsc : PromoClipsQuerySort.CreatedDesc
    );
  }, []);

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const onStatusChange = useCallback((status: AdCampaignStatusesEnum, isSelected: boolean) => {
    setSelectedStatuses((currentStatuses) => {
      if (isSelected) {
        return currentStatuses.includes(status) ? currentStatuses : [...currentStatuses, status];
      }

      return currentStatuses.filter((currentStatus) => currentStatus !== status);
    });
  }, []);

  const onPromoPlaybackToggle = useCallback(
    (promo: PromoListItem) => {
      if (!promo.audioUrl) {
        return;
      }

      play({
        fileUuid: promo.id,
        fileName: promo.title,
        fileUrl: promo.audioUrl,
      });
    },
    [play]
  );

  return {
    containerRef,
    triggerRef,
    dropdownId: DATA_TEST_IDS.AUDIO_REPORT_PROMOS_DROPDOWN,
    dropdownState,
    filterButtonRef,
    filterContainerRef,
    hasNextPage,
    hasLoadedFirstPage,
    isDropdownOpen,
    isDraftLoading,
    isFilterOpen,
    isInitialLoading,
    isNextPageLoading,
    isBulkSelectionActionPending,
    isSelectionActionPending,
    items,
    queryError: draftError || listQueryError,
    queryKey,
    search,
    selectedCount,
    selectedStatuses,
    sort,
    statusesInfo,
    totalCount,
    onDropdownToggle,
    onFilterToggle,
    onLoadMore,
    onSearchChange,
    onPromoPlaybackToggle,
    onPromoSelectionChange,
    onReset,
    onSelectAll,
    onRetry: draftError ? onRetryDraftCreate : onRetry,
    onSortToggle,
    onStatusChange,
  };
};
